import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useError } from '@client/hooks/useError';
import { useTenantQueries } from '@client/queries/useTenantQueries';
import { useLoading } from '@client/store/loading.store';
import { ERoutePaths } from '@client/types';
import {
  EErrorType,
  type ETier,
  type ITenant,
  type TErrorData,
} from '@common/types';

export const useTenant = () => {
  const {
    useUserTenant,
    useDeleteTenant,
    useUpdateTenant,
    useCreateTenant,
    useTierLimits,
  } = useTenantQueries();
  const { setError } = useError();
  const params = useParams();
  const navigate = useNavigate();

  const tenantId = params.tenantId as string;

  // Query for user tenants
  const {
    data: tenant,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
  } = useUserTenant(tenantId);
  const { setLoading } = useLoading(false);

  // Mutations
  const deleteMutation = useDeleteTenant();
  const createMutation = useCreateTenant();
  const updateMutation = useUpdateTenant();

  useEffect(() => {
    if (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.DOCUMENT_NOT_FOUND,
      });

      navigate(ERoutePaths.TENANTS);
    }
  }, [error, navigate, setError]);

  const onDelete = async (entity: ITenant): Promise<void> => {
    try {
      setLoading(true);
      await deleteMutation.mutateAsync(entity.id as string);
      navigate(ERoutePaths.TENANTS);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_DELETE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onUpdate = async (id: ITenant['id'], data: ITenant): Promise<void> => {
    try {
      setLoading(true);
      await updateMutation.mutateAsync({ id, data });
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_UPDATE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onCreate = async (data: ITenant): Promise<ITenant> => {
    try {
      setLoading(true);
      return createMutation.mutateAsync(data);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_CREATE,
      });
    } finally {
      setLoading(false);
    }
  };

  const getTierLimitsMutation = useTierLimits();
  const getTierLimits = async (tenant: ITenant, tier: ETier) => {
    try {
      setLoading(true);
      return await getTierLimitsMutation.mutateAsync({ tenant, tier });
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_GET,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading: isLoading || isFetching,
    isError,
    error,
    tenant,
    onDelete,
    onUpdate,
    onCreate,
    refetch,
    getTierLimits,
  };
};
